import { __decorate } from "tslib";
import React from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import omit from 'lodash/omit';
import shallowEqual from 'shallowequal';
import noop from 'lodash/noop';
import isElement from 'lodash/isElement';
import { PopupManager } from 'choerodon-ui/shared';
import ViewComponent from 'choerodon-ui/pro/lib/core/ViewComponent';
import autobind from 'choerodon-ui/pro/lib/_util/autobind';
import { findFocusableElements } from 'choerodon-ui/pro/lib/_util/focusable';
import { getDocument } from 'choerodon-ui/pro/lib/_util/DocumentUtils';
import Align from '../align';
import { getProPrefixCls } from '../configure/utils';
import Animate from '../animate';
import PopupInner from './PopupInner';
const childrenProps = { hidden: 'hidden' };
function newPopupContainer() {
    const doc = getDocument(window);
    const popupContainer = doc.createElement('div');
    popupContainer.className = getProPrefixCls('popup-container');
    return popupContainer;
}
let Popup = class Popup extends ViewComponent {
    constructor() {
        super(...arguments);
        this.contentRendered = false;
        this.popupKey = PopupManager.getKey();
        this.saveRef = align => (this.align = align);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'align',
            'transitionName',
            'getRootDomNode',
            'getPopupContainer',
            'getClassNameFromAlign',
            'getStyleFromAlign',
            'onAlign',
            'onAnimateAppear',
            'onAnimateEnter',
            'onAnimateLeave',
            'onAnimateEnd',
            'getFocusableElements',
            'forceRender',
        ]);
    }
    componentWillUnmount() {
        const { popupContainer } = this;
        if (popupContainer && popupContainer !== PopupManager.container && popupContainer.parentNode) {
            popupContainer.parentNode.removeChild(popupContainer);
        }
    }
    componentDidUpdate() {
        this.findFocusableElements();
    }
    componentDidMount() {
        super.componentDidMount();
        this.findFocusableElements();
    }
    findFocusableElements() {
        const { element } = this;
        const { getFocusableElements } = this.props;
        if (element && getFocusableElements) {
            const elements = findFocusableElements(element);
            getFocusableElements(elements && elements.filter(item => item.tabIndex !== -1).sort((e1, e2) => e1.tabIndex - e2.tabIndex));
        }
    }
    renderInner(innerRef) {
        const { children, getClassNameFromAlign = noop, align } = this.props;
        const className = this.getMergedClassNames(this.currentAlignClassName ||
            getClassNameFromAlign(align));
        return (React.createElement(PopupInner, Object.assign({}, omit(this.getMergedProps(), ['ref', 'className']), { className: className, innerRef: innerRef, onResize: this.handlePopupResize }), children));
    }
    render() {
        const { hidden, align, transitionName, getRootDomNode, forceRender, onAnimateAppear = noop, onAnimateEnter = noop, onAnimateLeave = noop, onAnimateEnd = noop, } = this.props;
        if (!hidden || forceRender) {
            this.contentRendered = true;
        }
        const container = this.getContainer();
        return container && this.contentRendered ? createPortal(React.createElement(Animate, { component: "", exclusive: true, transitionAppear: true, transitionName: transitionName, hiddenProp: "hidden", onAppear: onAnimateAppear, onEnter: onAnimateEnter, onLeave: onAnimateLeave, onEnd: onAnimateEnd },
            React.createElement(Align, { ref: this.saveRef, childrenRef: this.elementReference, key: "align", childrenProps: childrenProps, align: align, onAlign: this.onAlign, target: getRootDomNode, hidden: hidden, monitorWindowResize: true }, this.renderInner)), container, this.popupKey) : null;
    }
    getContainer() {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const { getPopupContainer, getRootDomNode = noop } = this.props;
        const globalContainer = PopupManager.container;
        if (getPopupContainer) {
            const container = this.popupContainer;
            if (container) {
                return container;
            }
        }
        else if (globalContainer) {
            return globalContainer;
        }
        if (getPopupContainer) {
            const mountNode = getPopupContainer(getRootDomNode());
            const popupContainer = newPopupContainer();
            const root = window.document.body;
            if (mountNode === root) {
                if (globalContainer) {
                    this.popupContainer = globalContainer;
                    return globalContainer;
                }
                PopupManager.container = popupContainer;
            }
            (mountNode && isElement(mountNode) ? mountNode : root).appendChild(popupContainer);
            this.popupContainer = popupContainer;
            return popupContainer;
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return getGlobalPopupContainer();
    }
    onAlign(source, align, target, translate) {
        const { getClassNameFromAlign = noop, getStyleFromAlign = noop, onAlign = noop } = this.props;
        const currentAlignClassName = getClassNameFromAlign(align);
        const differentTarget = target !== this.target;
        if (differentTarget || this.currentAlignClassName !== currentAlignClassName) {
            this.currentAlignClassName = currentAlignClassName;
            source.className = this.getMergedClassNames(currentAlignClassName);
        }
        const currentAlignStyle = getStyleFromAlign(target, align);
        if (differentTarget || !shallowEqual(this.currentAlignStyle, currentAlignStyle)) {
            this.currentAlignStyle = currentAlignStyle;
            Object.assign(source.style, currentAlignStyle);
        }
        onAlign(source, align, target, translate);
        this.target = source;
    }
    handlePopupResize(width, height) {
        if (width !== 0 && height !== 0) {
            const { width: oldWidth, height: oldHeight } = this.size || {};
            if (width !== oldWidth || height !== oldHeight) {
                this.size = { width, height };
                this.forceAlign();
            }
        }
    }
    forceAlign() {
        if (this.align) {
            this.align.forceAlign();
        }
    }
};
Popup.displayName = 'Popup';
Popup.defaultProps = {
    suffixCls: 'popup',
    transitionName: 'zoom',
};
__decorate([
    autobind
], Popup.prototype, "renderInner", null);
__decorate([
    autobind
], Popup.prototype, "onAlign", null);
__decorate([
    autobind
], Popup.prototype, "handlePopupResize", null);
Popup = __decorate([
    observer
], Popup);
export default Popup;
export function getGlobalPopupContainer() {
    if (PopupManager.container) {
        return PopupManager.container;
    }
    const popupContainer = newPopupContainer();
    const root = getDocument(window).body;
    root.appendChild(popupContainer);
    PopupManager.container = popupContainer;
    return popupContainer;
}
